import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  Client,
  IFrame,
  IMessage,
  StompSubscription,
} from '@stomp/stompjs';
import { EMPTY, Observable, ReplaySubject } from 'rxjs';
import { IFullWhatsappMessage } from 'projects/mind-your-tax/myt/modules/whatsapp/modules/messenger/model/whatsapp.model';
import { environment } from 'projects/mind-your-tax/src/environments/environment';

/**
 * After this many consecutive failed WS handshakes we stop trying to
 * reconnect. Prevents the "infinite loop" symptom on localhost when no
 * STOMP backend is running — without a cap, StompJS would retry every
 * `reconnectDelay` ms (5s) forever, flooding the console and chewing
 * CPU. In production the broker should be reachable on first attempt;
 * if it isn't, 5 retries gives ~25s of "we tried" before giving up.
 * User can recover by calling `connect()` again (e.g. a Reconnect
 * button) or by refreshing the page.
 */
const MAX_RECONNECT_ATTEMPTS = 5;

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private readonly isBrowser: boolean;
  private readonly allNotificationsSubject = new ReplaySubject<any>(1);
  private readonly ownNotificationsSubject = new ReplaySubject<any>(1);
  private readonly whatsappMessagesSubject =
    new ReplaySubject<IFullWhatsappMessage>(1);
  private client?: Client;
  private connectRequestCount = 0;
  private allSubscription?: StompSubscription;
  private ownSubscription?: StompSubscription;
  private whatsappSubscription?: StompSubscription;
  private consecutiveFailures = 0;
  private gaveUp = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  connect(): void {
    if (!this.isBrowser) return;

    // A previous run already exhausted the retry budget — calling
    // connect() again is the user's explicit "try again" signal so we
    // reset the failure counter and let the client re-activate.
    if (this.gaveUp) {
      console.log(
        '[WebSocket] connect() resetting after previous give-up; will retry',
      );
      this.gaveUp = false;
      this.consecutiveFailures = 0;
      if (this.client) {
        this.client.reconnectDelay = 5000;
      }
    }

    this.connectRequestCount += 1;
    console.log(
      '[WebSocket] connect requested',
      'refCount=', this.connectRequestCount,
      'url=', this.websocketUrl(),
    );
    this.ensureClient();

    if (!this.client?.active) {
      console.log('[WebSocket] activating client');
      this.client?.activate();
    }
  }

  disconnect(): void {
    if (!this.isBrowser) return;

    if (this.connectRequestCount > 0) {
      this.connectRequestCount -= 1;
    }

    console.log(
      '[WebSocket] disconnect requested',
      'refCount=', this.connectRequestCount,
    );

    if (this.connectRequestCount > 0) {
      return;
    }

    this.unsubscribeAll();
    if (this.client?.active) {
      console.log('[WebSocket] deactivating client');
      void this.client.deactivate();
    }
  }

  getAllNotifications(): Observable<any> {
    return this.isBrowser ? this.allNotificationsSubject.asObservable() : EMPTY;
  }

  getOwnNotifications(): Observable<any> {
    return this.isBrowser ? this.ownNotificationsSubject.asObservable() : EMPTY;
  }

  getWhatsappMessages(): Observable<IFullWhatsappMessage> {
    return this.isBrowser ? this.whatsappMessagesSubject.asObservable() : EMPTY;
  }

  private ensureClient(): void {
    if (this.client) {
      return;
    }

    this.client = new Client({
      webSocketFactory: () => new WebSocket(this.websocketUrl()),
      reconnectDelay: 5000,
      heartbeatIncoming: 30000,
      heartbeatOutgoing: 30000,
      connectionTimeout: 10000,
      discardWebsocketOnCommFailure: true,
      connectHeaders: {},
      onConnect: (frame: IFrame): void => {
        console.log('[WebSocket] connected', frame.headers);
        // A successful handshake resets the consecutive-failure budget
        // so a flaky network that recovers after a few retries doesn't
        // accidentally trip the give-up threshold next time it blips.
        this.consecutiveFailures = 0;
        this.subscribeToChannels();
      },
      onStompError: (frame: IFrame): void => {
        console.error(
          '[WebSocket] broker error',
          frame.headers,
          frame.body
        );
      },
      onWebSocketError: (event: Event): void => {
        console.error('[WebSocket] socket error', event);
      },
      onWebSocketClose: (event: CloseEvent): void => {
        console.warn(
          '[WebSocket] socket closed',
          event.code,
          event.reason
        );
        this.unsubscribeAll();
        this.handleConnectionFailure();
      },
    });
  }

  private subscribeToChannels(): void {
    if (!this.client?.connected) {
      console.warn('[WebSocket] subscribeToChannels skipped — client not connected');
      return;
    }

    this.unsubscribeAll();

    console.log('[WebSocket] subscribing to /all/messages, /user/specific, /whatsapp/message');

    this.allSubscription = this.client.subscribe(
      '/all/messages',
      (message: IMessage) => {
        const body = this.parseBody(message);
        console.log('[WebSocket] /all/messages →', body);
        this.allNotificationsSubject.next(body);
      }
    );

    this.ownSubscription = this.client.subscribe(
      '/user/specific',
      (message: IMessage) => {
        const body = this.parseBody(message);
        console.log('[WebSocket] /user/specific →', body);
        this.ownNotificationsSubject.next(body);
      }
    );

    this.whatsappSubscription = this.client.subscribe(
      '/whatsapp/message',
      (message: IMessage) => {
        const body = this.parseBody(message) as IFullWhatsappMessage;
        console.log('[WebSocket] /whatsapp/message →', body);
        this.whatsappMessagesSubject.next(body);
      }
    );
  }

  private unsubscribeAll(): void {
    if (
      this.allSubscription ||
      this.ownSubscription ||
      this.whatsappSubscription
    ) {
      console.log('[WebSocket] unsubscribing all channels');
    }
    this.allSubscription?.unsubscribe();
    this.allSubscription = undefined;
    this.ownSubscription?.unsubscribe();
    this.ownSubscription = undefined;
    this.whatsappSubscription?.unsubscribe();
    this.whatsappSubscription = undefined;
  }

  private websocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const isLocalDev =
      !environment.production && window.location.hostname === 'localhost';

    if (isLocalDev) {
      return `${protocol}${window.location.host}/api/ws`;
    }

    return `${protocol}${environment.endPoint}/api/ws`;
  }

  private parseBody(message: IMessage): any {
    try {
      return JSON.parse(message.body);
    } catch {
      return message.body;
    }
  }

  /**
   * Called from `onWebSocketClose` after every socket close. Increments
   * the consecutive-failure counter and, once it crosses
   * `MAX_RECONNECT_ATTEMPTS`, sets `reconnectDelay = 0` and deactivates
   * the client so StompJS stops re-scheduling failed reconnects. The
   * "infinite loop on localhost" was just StompJS faithfully retrying
   * every 5s forever — this caps it.
   */
  private handleConnectionFailure(): void {
    if (this.gaveUp || !this.client) {
      return;
    }

    this.consecutiveFailures += 1;

    if (this.consecutiveFailures < MAX_RECONNECT_ATTEMPTS) {
      console.warn(
        '[WebSocket] reconnect attempt',
        `${this.consecutiveFailures}/${MAX_RECONNECT_ATTEMPTS}`,
      );
      return;
    }

    console.warn(
      `[WebSocket] giving up after ${MAX_RECONNECT_ATTEMPTS} consecutive failed connects — ` +
        'call connect() again (e.g. refresh) to retry.',
    );
    this.gaveUp = true;
    this.client.reconnectDelay = 0;
    void this.client.deactivate();
  }
}
